import { GridElement } from './types';

interface HeaderColumnProps {
  label: string;
  width?: number;
  notReadOnly?: boolean;
  nested?: HeaderColumn[];
}

export type HeaderColumn = HeaderColumnProps | string;

const getMaxDepth = (columns: HeaderColumn[]): number => {
  return columns.reduce((max, column) => {
    if (typeof column === 'string') {
      return max;
    }
    if (column.nested) {
      return Math.max(max, 1 + getMaxDepth(column.nested));
    }
    return max;
  }, 1);
};

const getDepth = (column: HeaderColumn, current: number = 1): number => {
  if (typeof column === 'string') {
    return current;
  }
  if (column.nested) {
    return current + getMaxDepth(column.nested);
  }
  return current;
};

function getValue(column: HeaderColumn, depth: number, currentDepth: number = 1): HeaderColumn[] {
  if (typeof column === 'string') {
    if (depth === currentDepth) {
      return [column];
    }
    return [];
  }

  if (depth === currentDepth) {
    return [column];
  }

  if (column.nested) {
    return column.nested.flatMap((c) => getValue(c, depth, currentDepth + 1));
  }
  return [];
}

export function createHeader(columns: HeaderColumn[], height: number, freezeX: number): GridElement[][] {
  // nestedのmaxDepthを取得
  const maxDepth = getMaxDepth(columns);
  const headers: GridElement[][] = [];
  const nextIndex = [0, 0, 0, 0, 0];
  for (let depth = 1; depth <= maxDepth; depth++) {
    const colm: HeaderColumn[] = columns.flatMap((c) => getValue(c, depth));
    const header = colm.map((col, idx) => {
      const currentDepth = getDepth(col, depth);
      const rowSpan = maxDepth - (currentDepth - 1);
      // eslint-disable-next-line no-nested-ternary
      const colSpan = typeof col === 'string' ? 1 : col.nested === undefined ? 1 : col.nested.length;
      const index = idx + (currentDepth > 1 ? nextIndex[currentDepth - 1] : 0) + 1;

      const column: GridElement = {
        value: typeof col === 'string' ? col : col.label,
        readOnly: typeof col === 'string' ? true : col.notReadOnly !== true,
        type: 'header',
        isNew: false,
        width: typeof col === 'string' ? 0 : (col.width ?? 0),
        height,
        freezeY: true,
        freezeX: index <= freezeX,
      };
      if (rowSpan > 1) {
        column.rowSpan = rowSpan;
        nextIndex[depth]++;
      }
      if (colSpan > 1) {
        column.colSpan = colSpan;
      }
      return column;
    });
    headers.push(header);
  }
  return headers;
}
